/**
 * Industries (`/industries`) — under Reference Data, for everyone who may view
 * them. Each control follows its own permission: Add (`industry.create`), Edit
 * and the Status switch (`industry.update`), Approve and Reject
 * (`industry.review`). Without them the same list reads, pending proposals
 * included, with no controls (the server refuses the writes anyway).
 *
 * The house list layout (as Team & Access, without a tab bar): one white card
 * holding the toolbar (search, status filter, Add industry), a
 * plain-header sortable table and the pagination. The list is small, so it all
 * runs in the browser. One table holds every industry; its status says which:
 *
 * - Active / Inactive: approved industries, offered on the submit form while
 *   active. Never deleted: deactivate and reactivate them.
 * - Pending review: industries research proposed when nothing fitted. Their
 *   ideas already carry them. Approve makes one a normal industry; Reject
 *   moves its ideas to an approved industry you choose (none to move once no
 *   idea uses it) and removes it; research may propose the name again for a different idea. A banner over
 *   the table counts them and filters to them.
 */

import { Plus, Search } from 'lucide-react';

import type { Industry } from '@/api/types';
import { ConfirmDialog } from '@/ui/ConfirmDialog';
import { MultiSelect } from '@/ui/MultiSelect';
import { Pagination } from '@/ui/Pagination';
import { RefreshButton } from '@/ui/RefreshButton';
import { PageHead } from '@/ui/PageHead';
import { LoadFailure, LoadingCard } from '@/ui/StateCard';
import { IndustriesTable, PendingBanner } from './IndustriesTable';
import { IndustryDialog } from './IndustryDialog';
import type { IndustryRowActions } from './IndustryRow';
import { RejectIndustryDialog } from './RejectIndustryDialog';
import { STATUSES } from './industryList';
import { useIndustriesPage, type IndustriesPageState } from './useIndustriesPage';

function Toolbar({ state }: { state: IndustriesPageState }) {
  return (
    <div className="toolbar filter-panel">
      <div className="search-field">
        <Search aria-hidden="true" />
        <input
          type="search"
          value={state.search}
          placeholder="Search industries"
          aria-label="Search industries"
          onChange={(e) => state.setSearch(e.target.value)}
        />
      </div>
      <MultiSelect
        label="Filter by status"
        allLabel="All statuses"
        noun="statuses"
        options={STATUSES.map((s) => ({ ...s, meta: state.countOf(s.value) }))}
        value={state.statuses}
        onChange={state.setStatuses}
      />
      <RefreshButton iconOnly queryKeys={[['industries']]} />
      {state.canCreate && (
        <button type="button" className="btn btn-primary" onClick={() => state.setDialog({ kind: 'add' })}>
          <Plus aria-hidden="true" /> Add industry
        </button>
      )}
    </div>
  );
}

function ApproveDialog({ industry, state }: { industry: Industry; state: IndustriesPageState }) {
  const { approve, setApproving } = state;
  return (
    <ConfirmDialog
      title={`Approve "${industry.name}"?`}
      confirmLabel="Approve industry"
      pending={approve.isPending}
      error={approve.error}
      onClose={() => {
        approve.reset();
        setApproving(null);
      }}
      onConfirm={() => approve.mutate(industry.id, { onSuccess: () => setApproving(null) })}
    >
      <p>
        It becomes a normal industry, offered on the submit form. Its {industry.idea_count} idea(s) keep it, still marked as
        assigned by AI research.
      </p>
    </ConfirmDialog>
  );
}

function Dialogs({ state }: { state: IndustriesPageState }) {
  const { dialog, approving } = state;
  const close = () => state.setDialog(null);
  return (
    <>
      {dialog?.kind === 'add' && <IndustryDialog onClose={close} />}
      {dialog?.kind === 'edit' && <IndustryDialog industry={dialog.industry} onClose={close} />}
      {dialog?.kind === 'reject' && <RejectIndustryDialog industry={dialog.industry} choices={state.choices} onClose={close} />}
      {approving && <ApproveDialog industry={approving} state={state} />}
    </>
  );
}

function rowActions(state: IndustriesPageState): IndustryRowActions {
  return {
    canUpdate: state.canUpdate,
    canReview: state.canReview,
    withActions: state.withActions,
    busy: state.save.isPending,
    onToggle: (industry, active) => state.save.mutate({ id: industry.id, change: { is_active: active } }),
    onApprove: state.setApproving,
    onReject: (industry) => state.setDialog({ kind: 'reject', industry }),
    onEdit: (industry) => state.setDialog({ kind: 'edit', industry }),
  };
}

function IndustriesList({ state }: { state: IndustriesPageState }) {
  const { statuses, mutationError, visible } = state;
  const pendingCount = state.countOf('pending');
  const showingPending = statuses.length === 1 && statuses[0] === 'pending';
  return (
    <div className="card list-card">
      <Toolbar state={state} />

      {pendingCount > 0 && (
        <PendingBanner
          pendingCount={pendingCount}
          canReview={state.canReview}
          showingPending={showingPending}
          onToggle={() => state.setStatuses(showingPending ? [] : ['pending'])}
        />
      )}

      {mutationError && (
        <div className="alert alert-warning form-alert" role="alert">
          <div className="a-body">{mutationError.message}</div>
        </div>
      )}

      <IndustriesTable
        sort={state.sort}
        onSort={state.onSort}
        pageRows={state.pageRows}
        noIndustries={state.rows.length === 0}
        actions={rowActions(state)}
      />
      {visible.length > 0 && (
        <Pagination
          page={state.page}
          pageSize={state.pageSize}
          total={visible.length}
          noun="industries"
          onPage={state.setPage}
          onPageSize={state.setPageSize}
        />
      )}
    </div>
  );
}

export function IndustriesPage() {
  const state = useIndustriesPage();
  const { industries } = state;
  const head = <PageHead title="Industries" subtitle="The industries ideas are filed under, and the ones research proposed" />;

  if (industries.isPending) return <>{head}<LoadingCard label="Loading industries" /></>;
  if (industries.error) return <>{head}<LoadFailure error={industries.error} onRetry={() => void industries.refetch()} /></>;

  return (
    <>
      {head}
      <IndustriesList state={state} />
      <Dialogs state={state} />
    </>
  );
}
